/*
 * WL shop grid: section (WL / MAINLINE / SPORTS) × product-type category.
 *
 * The collection template renders the current collection's product cards.
 * This script pulls the section's other source collections through the
 * Section Rendering API, merges everything into one product list, and then
 * filters / sorts / paginates on the client. Rail, chip and pagination links
 * are real links (progressive enhancement) that are intercepted for
 * client-side navigation with history.pushState.
 *
 * Products on sale (compare-at price above price) only appear in the "new"
 * category (the sale tab); they are hidden from the product-type categories.
 * When the "new" tab contains sale items, it gets a dropdown that filters it
 * by product type (?sub=tees, ?sub=hoodies, ...).
 */
(() => {
  const root = document.querySelector('[data-wl-shop]');
  if (!root) return;

  const configEl = root.querySelector('[data-wl-shop-config]');
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    console.error('[wl-shop] invalid config', error);
    return;
  }

  const grid = root.querySelector('[data-wl-grid]');
  const rail = root.querySelector('[data-wl-rail]');
  const chips = root.querySelector('[data-wl-chips]');
  const status = root.querySelector('[data-wl-status]');
  const empty = root.querySelector('[data-wl-empty]');
  const pagination = root.querySelector('[data-wl-pagination]');
  const teams = root.querySelector('[data-wl-teams]');
  const teamSelect = teams ? teams.querySelector('[data-wl-team-select]') : null;
  const teamOptions = teamSelect ? Array.from(teamSelect.options).filter((o) => o.value) : [];
  if (!grid) return;

  const categories = Array.isArray(config.categories) ? config.categories : [];
  const categoryKeys = new Set(categories.map((c) => c.key));
  const typeMap = new Map();
  categories.forEach((category) => {
    (category.types || []).forEach((type) => typeMap.set(normalizeType(type), category.key));
  });
  // product-type categories usable as sale sub-filters
  const subCategories = categories.filter((c) => c.key !== 'new' && c.key !== 'all');
  const subKeys = new Set(subCategories.map((c) => c.key));

  const sources = (config.sources || []).map((s) => String(s || '').trim()).filter(Boolean);
  const sourceSet = new Set(sources);
  const newHandle = String(config.newHandle || '').trim();
  const defaultCat = categoryKeys.has(config.defaultCat) ? config.defaultCat : 'new';
  const perPage = Math.max(1, Number(config.perPage) || 32);
  const newLimit = Math.max(0, Number(config.newLimit) || 0);
  const initialPath = location.pathname;
  // 16 section pages per source collection (512 products at 32 per page)
  const MAX_PAGES_PER_SOURCE = 16;
  // v3: cards now carry data-tags (team filter); bumping the prefix drops older cached markup
  const CACHE_PREFIX = `wl-shop:v3:${config.sectionId}:`;
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const CACHE_MAX_BYTES = 900 * 1024;

  // Title-based fallback when the product type is blank or not mapped.
  // Order matters: earlier rules win ("Trucker Hat" → hats, not jackets).
  const TITLE_RULES = [
    ['hats', /\b(hats?|caps?|beanies?|visors?|snapbacks?|bucket hat)\b/i],
    ['hoodies', /\b(hoodies?|sweatshirts?|crewnecks?|crew neck|zip[- ]?ups?|pullovers?|sweaters?|thermals?)\b/i],
    ['jackets', /\b(jackets?|flannels?|truckers?|coats?|parkas?|vests?|windbreakers?|anoraks?|overshirts?|puffers?|varsity)\b/i],
    ['bottoms', /\b(pants?|shorts?|sweatpants?|sweatshorts?|joggers?|denim|jeans?|trousers?|cargos?|bottoms?)\b/i],
    ['tees', /\b(t-?shirts?|tees?|tanks?|tank tops?|jerseys?|polos?|shirts?|long ?sleeves?|ringers?|tops?)\b/i],
    [
      'accessories',
      /\b(socks?|bags?|wallets?|masks?|gloves?|stickers?|toys?|shoes?|keychains?|pins?|towels?|blankets?|gift cards?|slides?|sandals?|belts?|scarf|scarves|lanyards?|patch(es)?|posters?|accessor(y|ies))\b/i,
    ],
  ];

  function normalizeType(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function categorize(product) {
    const mapped = typeMap.get(product.type);
    if (mapped) return mapped;
    for (const [key, pattern] of TITLE_RULES) {
      if (pattern.test(product.title)) return categoryKeys.has(key) ? key : null;
    }
    return null;
  }

  // ---- sale dropdown (built on the "new" rail link) ----
  const drop = (() => {
    const saleLink = rail && rail.querySelector('a[data-wl-cat="new"]');
    if (!saleLink || !subCategories.length) return null;

    const style = document.createElement('style');
    style.textContent = `
      .wl-shop-rail.has-wl-drop { overflow: visible; }
      .wl-shop-drop { display: inline-flex; align-items: center; gap: 6px; position: relative; }
      .wl-shop-drop__toggle {
        align-items: center; background: none; border: 0; color: inherit; cursor: pointer;
        display: inline-flex; height: 24px; justify-content: center; margin: 0; padding: 0; width: 18px;
      }
      .wl-shop-drop__toggle[hidden] { display: none; }
      .wl-shop-drop__toggle svg { transition: transform 160ms ease; }
      .wl-shop-drop.is-open .wl-shop-drop__toggle svg { transform: rotate(180deg); }
      .wl-shop-drop__menu {
        display: none; left: 50%; min-width: 180px; padding-top: 10px; position: absolute;
        top: 100%; transform: translateX(-50%); z-index: 30;
      }
      .wl-shop-drop.has-menu.is-open .wl-shop-drop__menu { display: block; }
      @media (hover: hover) {
        .wl-shop-drop.has-menu:hover .wl-shop-drop__menu,
        .wl-shop-drop.has-menu:focus-within .wl-shop-drop__menu { display: block; }
      }
      .wl-shop-drop__list {
        background: var(--color-background, #fff); border: 1px solid var(--color-foreground, #000);
        list-style: none; margin: 0; padding: 8px 0; text-align: center;
      }
      .wl-shop-drop__item { display: block; padding: 8px 18px; white-space: nowrap; color: rgb(0 0 0 / 55%); }
      .wl-shop-drop__item:hover, .wl-shop-drop__item:focus-visible, .wl-shop-drop__item[aria-current='page'] {
        color: var(--color-foreground, #000); text-decoration: underline; text-underline-offset: 2px;
      }
      @media (max-width: 749px) { .wl-shop-drop__menu { min-width: 160px; } }
    `;
    document.head.appendChild(style);

    const wrap = document.createElement('span');
    wrap.className = 'wl-shop-drop';
    saleLink.replaceWith(wrap);
    wrap.appendChild(saleLink);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'wl-shop-drop__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', `Filter ${saleLink.textContent.trim()} by category`);
    toggle.innerHTML =
      '<svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    toggle.style.color = getComputedStyle(saleLink).color;
    toggle.hidden = true;

    const menu = document.createElement('div');
    menu.className = 'wl-shop-drop__menu';
    const list = document.createElement('ul');
    list.className = 'wl-shop-drop__list';
    menu.appendChild(list);
    wrap.append(toggle, menu);

    function setOpen(open) {
      wrap.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      setOpen(!wrap.classList.contains('is-open'));
    });
    document.addEventListener('click', (event) => {
      if (!wrap.contains(event.target)) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && wrap.classList.contains('is-open')) {
        setOpen(false);
        toggle.focus();
      }
    });

    let lastSignature = '';
    function update(state, baseList, partial) {
      if (partial) return;
      const hasSale = baseList.some((p) => p.sale);
      const counts = new Map();
      baseList.forEach((p) => counts.set(p.cat, (counts.get(p.cat) || 0) + 1));
      const present = subCategories.filter((c) => counts.get(c.key));
      const enabled = hasSale && present.length > 1;
      wrap.classList.toggle('has-menu', enabled);
      rail.classList.toggle('has-wl-drop', enabled);
      toggle.hidden = !enabled;
      if (!enabled) {
        setOpen(false);
        list.replaceChildren();
        lastSignature = '';
        return;
      }
      const activeSub = state.cat === 'new' ? state.sub : '';
      const signature = `${present.map((c) => c.key).join(',')}|${state.cat}|${activeSub}|${state.src}`;
      if (signature === lastSignature) return;
      lastSignature = signature;
      const frag = document.createDocumentFragment();
      [{ key: '', label: 'All' }, ...present].forEach((c) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'wl-shop-drop__item';
        a.dataset.wlSub = c.key;
        a.href = buildUrl({ cat: 'new', src: state.cat === 'new' ? state.src : '', page: 1, sub: c.key });
        a.textContent = c.label;
        if (state.cat === 'new' && c.key === activeSub) a.setAttribute('aria-current', 'page');
        li.appendChild(a);
        frag.appendChild(li);
      });
      list.replaceChildren(frag);
    }

    return { update, close: () => setOpen(false) };
  })();

  // ---- product store ----
  const products = new Map(); // handle -> product record
  const order = []; // insertion order (stable tiebreak)
  const sourceOrder = new Map(); // source handle -> [product handles in collection order]
  const loaded = new Set();
  const loading = new Map();

  function ingest(cards, source) {
    const list = sourceOrder.get(source) || [];
    sourceOrder.set(source, list);
    const sourceIndex = Math.max(0, sources.indexOf(source));
    cards.forEach((el) => {
      const handle = el.dataset.handle;
      if (!handle) return;
      let product = products.get(handle);
      if (!product) {
        const published = Number(el.dataset.published) || 0;
        product = {
          handle,
          el,
          type: normalizeType(el.dataset.type),
          title: el.dataset.title || '',
          published,
          // drops are published in batches; group by day so curated order survives within a drop
          publishedDay: Math.floor(published / 86400),
          available: el.dataset.available === 'true',
          sale: el.dataset.sale === 'true',
          tags: new Set(String(el.dataset.tags || '').split('|').map((t) => t.trim().toLowerCase()).filter(Boolean)),
          sources: new Set(),
          rank: Number.POSITIVE_INFINITY,
        };
        product.cat = categorize(product);
        products.set(handle, product);
        order.push(product);
      }
      if (source && !product.sources.has(source)) {
        product.sources.add(source);
        list.push(handle);
        // deterministic tiebreak: earlier source, then collection position
        product.rank = Math.min(product.rank, sourceIndex * 100000 + list.length);
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  // ---- fetching ----
  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > CACHE_TTL_MS) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return entry.html;
    } catch {
      return null;
    }
  }

  function writeCache(key, html) {
    if (!html || html.length > CACHE_MAX_BYTES) return;
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), html }));
    } catch {
      // quota exceeded or storage disabled – purge our keys and move on
      try {
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith('wl-shop:'))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch {
        /* ignore */
      }
    }
  }

  function sectionUrl(handle, page) {
    const url = new URL(`${config.collectionsUrl}/${handle}`, location.origin);
    url.searchParams.set('section_id', config.sectionId);
    if (page > 1) url.searchParams.set('page', String(page));
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.toString();
  }

  async function fetchSectionHtml(handle, page) {
    const key = `${handle}:${page}`;
    const cached = readCache(key);
    if (cached) return cached;
    const response = await fetch(sectionUrl(handle, page), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`[wl-shop] ${handle} page ${page}: HTTP ${response.status}`);
    const html = await response.text();
    writeCache(key, html);
    return html;
  }

  function loadSource(handle) {
    if (loaded.has(handle)) return Promise.resolve();
    if (loading.has(handle)) return loading.get(handle);
    const task = (async () => {
      let page = 1;
      let pages = 1;
      try {
        do {
          const html = await fetchSectionHtml(handle, page);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedGrid = doc.querySelector('[data-wl-grid]');
          if (!fetchedGrid) break;
          pages = Math.max(1, Number(fetchedGrid.dataset.wlPages) || 1);
          ingest(Array.from(fetchedGrid.querySelectorAll('[data-wl-product]')), handle);
          page += 1;
        } while (page <= pages && page <= MAX_PAGES_PER_SOURCE);
      } catch (error) {
        console.warn(error);
      } finally {
        loaded.add(handle);
        loading.delete(handle);
      }
    })();
    loading.set(handle, task);
    return task;
  }

  // ---- state / urls ----
  function readState() {
    const params = new URLSearchParams(location.search);
    let cat = params.get('cat') || defaultCat;
    if (!categoryKeys.has(cat)) cat = defaultCat;
    let src = params.has('src')
      ? params.get('src') || ''
      : location.pathname === initialPath
        ? config.implicitSource || ''
        : '';
    if (src && !sourceSet.has(src)) src = '';
    let sub = params.get('sub') || '';
    if (cat !== 'new' || !subKeys.has(sub)) sub = '';
    let page = parseInt(params.get('page') || '1', 10);
    if (!(page > 0)) page = 1;
    // team filter (chip sections): only valid for a team listed under the active league
    let team = params.get('team') || '';
    if (!src || !teamOptions.some((o) => o.dataset.league === src && o.value.toLowerCase() === team.toLowerCase())) team = '';
    return { cat, src, page, sub, team };
  }

  function buildUrl(state) {
    const params = new URLSearchParams();
    if (state.cat && state.cat !== defaultCat) params.set('cat', state.cat);
    if (state.cat === 'new' && state.sub) params.set('sub', state.sub);
    if (state.src) params.set('src', state.src);
    if (state.src && state.team) params.set('team', state.team);
    if (state.page > 1) params.set('page', String(state.page));
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) params.set('preview_theme_id', preview);
    const query = params.toString();
    return `${config.basePath}${query ? `?${query}` : ''}`;
  }

  function neededSources(state) {
    if (state.src) return [state.src];
    if (state.cat === 'new' && newHandle) return [newHandle];
    return sources.length ? sources : [config.currentHandle];
  }

  // ---- list computation ----
  function computeList(state) {
    let list;
    if (state.cat === 'new' && newHandle && !state.src) {
      list = (sourceOrder.get(newHandle) || []).map((h) => products.get(h)).filter(Boolean);
    } else {
      const showAll = state.cat === 'new' || state.cat === 'all';
      list = order.filter((p) => showAll || p.cat === state.cat);
      // sale items live only in the sale ("new") tab, not in product-type tabs
      if (!showAll) list = list.filter((p) => !p.sale);
      if (state.src) list = list.filter((p) => p.sources.has(state.src));
      if (state.src && state.team) {
        const teamTag = state.team.toLowerCase();
        list = list.filter((p) => p.tags.has(teamTag));
      }
      const curatedSingle = (state.src && state.cat === 'new') || (state.cat === 'all' && sources.length === 1);
      if (curatedSingle) {
        // one collection, "new arrivals": keep the merchant's collection order
        const ordered = sourceOrder.get(state.src || sources[0]) || [];
        const rank = new Map(ordered.map((h, i) => [h, i]));
        list.sort((a, b) => (rank.get(a.handle) ?? 1e9) - (rank.get(b.handle) ?? 1e9));
      } else {
        list.sort((a, b) => b.publishedDay - a.publishedDay || a.rank - b.rank);
      }
      if (state.cat === 'new' && newLimit > 0) list = list.slice(0, newLimit);
    }
    // sale dropdown: narrow the "new" tab to one product type
    if (state.cat === 'new' && state.sub) list = list.filter((p) => p.cat === state.sub);
    return list;
  }

  // ---- painting ----
  function updateRail(state) {
    if (!rail) return;
    // collection tabs in the rail (e.g. CLASSICS): a source link, not a product-type filter
    const railSources = Array.from(rail.querySelectorAll('[data-wl-src]'));
    const railSourceKeys = new Set(railSources.map((link) => link.dataset.wlSrc || ''));
    const railSourceActive = Boolean(state.src) && railSourceKeys.has(state.src);
    rail.querySelectorAll('[data-wl-cat]').forEach((link) => {
      const key = link.dataset.wlCat;
      link.href = buildUrl({ cat: key, src: railSourceKeys.has(state.src) ? '' : state.src, page: 1 });
      if (key === state.cat && !railSourceActive) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    railSources.forEach((link) => {
      const key = link.dataset.wlSrc || '';
      link.href = buildUrl({ cat: defaultCat, src: key, page: 1 });
      if (key === state.src) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function updateTeams(state, partial) {
    if (!teams || !teamSelect) return;
    const leagueOptions = teamOptions.filter((o) => o.dataset.league === state.src);
    const show = Boolean(state.src) && leagueOptions.length > 0;
    teams.hidden = !show;
    if (!show) return;
    // grey out teams with no live products once the league has loaded
    const counts = new Map();
    if (!partial) {
      order.forEach((p) => {
        if (!p.sources.has(state.src)) return;
        p.tags.forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
      });
    }
    // only grey out teams when the loaded cards actually carried tags;
    // tag-less markup (older cache, editor preview) leaves every team selectable
    const canDisable = !partial && counts.size > 0;
    teamOptions.forEach((o) => {
      const mine = o.dataset.league === state.src;
      o.hidden = !mine;
      o.disabled = mine && canDisable && !counts.get(o.value.toLowerCase());
    });
    const current = leagueOptions.find((o) => o.value.toLowerCase() === state.team.toLowerCase());
    teamSelect.value = current ? current.value : '';
  }

  function updateChips(state) {
    if (!chips) return;
    chips.querySelectorAll('[data-wl-src]').forEach((link) => {
      const key = link.dataset.wlSrc || '';
      link.href = buildUrl({ cat: state.cat, src: key, page: 1, sub: state.sub });
      if (key === state.src) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function renderPagination(page, pages, state) {
    if (!pagination) return;
    if (pages <= 1) {
      pagination.hidden = true;
      pagination.replaceChildren();
      return;
    }
    const frag = document.createDocumentFragment();
    const span = (className, child) => {
      const el = document.createElement('span');
      el.className = className;
      if (typeof child === 'string') el.textContent = child;
      else if (child) el.appendChild(child);
      return el;
    };
    const link = (label, target) => {
      const a = document.createElement('a');
      a.href = buildUrl({ ...state, page: target });
      a.dataset.wlPage = String(target);
      a.textContent = label;
      return a;
    };
    if (page > 1) frag.appendChild(span('prev', link('‹ Previous', page - 1)));
    const windowStart = Math.max(1, page - 2);
    const windowEnd = Math.min(pages, page + 2);
    if (windowStart > 1) {
      frag.appendChild(span('page', link('1', 1)));
      if (windowStart > 2) frag.appendChild(span('deco', '…'));
    }
    for (let i = windowStart; i <= windowEnd; i += 1) {
      if (i === page) frag.appendChild(span('page current', String(i)));
      else frag.appendChild(span('page', link(String(i), i)));
    }
    if (windowEnd < pages) {
      if (windowEnd < pages - 1) frag.appendChild(span('deco', '…'));
      frag.appendChild(span('page', link(String(pages), pages)));
    }
    if (page < pages) frag.appendChild(span('next', link('Next ›', page + 1)));
    pagination.replaceChildren(frag);
    pagination.hidden = false;
  }

  function paint(state, partial) {
    const list = computeList(state);
    if (drop) {
      const saleState = { cat: 'new', src: state.cat === 'new' ? state.src : '', page: 1, sub: '' };
      const saleReady = neededSources(saleState).every((h) => loaded.has(h));
      drop.update(state, saleReady ? computeList(saleState) : [], !saleReady);
    }
    const pages = Math.max(1, Math.ceil(list.length / perPage));
    const page = Math.min(state.page, pages);
    const slice = list.slice((page - 1) * perPage, page * perPage);
    const frag = document.createDocumentFragment();
    slice.forEach((p) => frag.appendChild(p.el));
    grid.replaceChildren(frag);
    const isEmpty = list.length === 0 && !partial;
    if (empty) {
      const emptyText = empty.querySelector('[data-wl-empty-text]');
      const hero = empty.querySelector('[data-wl-empty-hero]');
      // launch artwork: first tab only (no sub-filter, no collection tab), only while it has zero live products
      const useHero = Boolean(hero) && state.cat === 'new' && !state.sub && !state.src;
      if (hero) hero.hidden = !useHero;
      if (emptyText) {
        const message = state.cat === 'new' && !state.sub ? emptyText.dataset.wlEmptyNew : emptyText.dataset.wlEmptyDefault;
        if (message) emptyText.textContent = message;
        emptyText.hidden = useHero;
      }
      empty.hidden = !isEmpty;
    }
    renderPagination(page, pages, state);
    root.classList.add('is-ready');
    return { list, page, pages };
  }

  let renderToken = 0;
  async function render({ scroll = false } = {}) {
    const token = ++renderToken;
    const state = readState();
    updateRail(state);
    updateChips(state);

    const missing = neededSources(state).filter((h) => !loaded.has(h));
    if (missing.length) {
      root.classList.add('is-loading');
      if (status) status.hidden = false;
      paint(state, true);
      updateTeams(state, true);
      await Promise.all(missing.map(loadSource));
      if (token !== renderToken) return;
      root.classList.remove('is-loading');
      if (status) status.hidden = true;
    }
    paint(state, false);
    updateTeams(state, false);
    if (scroll) scrollToTop();
  }

  function scrollToTop() {
    const target = root.getBoundingClientRect().top + window.scrollY - 8;
    if (window.scrollY > target) window.scrollTo({ top: Math.max(0, target), behavior: 'auto' });
  }

  // ---- navigation ----
  root.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-wl-cat], a[data-wl-src], a[data-wl-sub], [data-wl-pagination] a');
    if (!link || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin) return;
    event.preventDefault();
    if (drop) drop.close();
    if (document.activeElement && link.contains(document.activeElement)) document.activeElement.blur();
    history.pushState({ wlShop: true }, '', url.pathname + url.search);
    render({ scroll: true });
  });

  window.addEventListener('popstate', () => {
    render({ scroll: false });
  });

  teamSelect?.addEventListener('change', () => {
    const state = readState();
    const url = buildUrl({ ...state, team: teamSelect.value, page: 1 });
    history.pushState({ wlShop: true }, '', url);
    render({ scroll: false });
  });

  // ---- boot ----
  const ssrSource = grid.dataset.wlSource || config.currentHandle;
  ingest(Array.from(grid.querySelectorAll('[data-wl-product]')), ssrSource);
  const ssrPages = Math.max(1, Number(grid.dataset.wlPages) || 1);
  if (ssrPages <= 1) loaded.add(ssrSource);
  render({ scroll: false });
})();